import { useRef, useState } from "react";
import { toast } from "sonner";
import { PortalModal, Field, DemoNotice } from "@/components/portal/PortalModal";
import { IN_STATES, LANGUAGES } from "@/config/regions";
import { submitInquiry, newIdempotencyKey, isLive } from "@/lib/platformClient";
import { useI18n } from "@/i18n/I18nProvider";

const TYPES = ["Wedding", "Corporate", "Social", "Other"];
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function validate(f) {
  const e = {};
  if (!f.city.trim()) e.city = "City is required.";
  if (f.guests && (!/^\d+$/.test(f.guests) || +f.guests < 1 || +f.guests > 100000)) e.guests = "Enter 1–100000 guests.";
  if (f.name.trim().length < 2) e.name = "Your name is required.";
  if (!EMAIL.test(f.email.trim())) e.email = "Enter a valid email.";
  if (f.mobile && !/^[6-9]\d{9}$/.test(f.mobile)) e.mobile = "Enter a 10-digit Indian mobile number.";
  if (!f.consent) e.consent = "Consent is required to submit.";
  return e;
}

export const InquiryDialog = ({ onClose, prefill = {} }) => {
  const { lang } = useI18n();
  const key = useRef(newIdempotencyKey());
  const [f, setF] = useState({ type: prefill.type || "Wedding", state: "TG", city: prefill.city || "", date: "", guests: prefill.guests || "", name: "", email: "", mobile: "", language: lang, notes: prefill.notes || "", consent: false, website: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (busy || result) return;
    if (f.website) return;
    const errs = validate(f);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const payload = { event_type: f.type, location: { country: "IN", state_code: f.state, city: f.city.trim() }, event_date: f.date || null, guests: f.guests ? +f.guests : null, contact: { name: f.name.trim(), email: f.email.trim(), mobile: f.mobile ? `+91${f.mobile}` : null }, preferred_language: f.language, notes: f.notes.trim(), consent: { contact: true, captured_at: new Date().toISOString() }, source: "ayndri-portal" };
      setResult(await submitInquiry(payload, key.current));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <PortalModal title="Inquiry received" onClose={onClose} testId="inquiry-dialog">
        <div className="done" data-testid="inquiry-result">
          <div className="done-check">✓</div>
          <h3>{result.mode === "demo" ? "Demo inquiry preview" : "Inquiry submitted"}</h3>
          <p>Reference: <b data-testid="inquiry-reference">{result.reference}</b></p>
          {result.mode === "demo" && <DemoNotice>No CRM lead was created. Live submission goes through the existing public Event Journey once its contract is verified.</DemoNotice>}
          <div className="modal-actions center"><button className="btn blue" onClick={onClose} data-testid="inquiry-done-button">Close</button></div>
        </div>
      </PortalModal>
    );
  }

  return (
    <PortalModal title="Event Inquiry — India Pilot" onClose={onClose} testId="inquiry-dialog">
      {!isLive("publicInquiry") && <DemoNotice>Submissions are previewed only. They will route to the existing Event Journey → CRM lead flow, with its consent, idempotency and reference rules, once verified.</DemoNotice>}
      <form onSubmit={submit} noValidate style={{ marginTop: 14 }}>
        <input className="hp" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} aria-hidden="true" name="website" />
        <div className="formgrid">
          <Field id="inq-type" label="Event type"><select id="inq-type" value={f.type} onChange={set("type")} data-testid="inquiry-type-select">{TYPES.map((t) => <option key={t}>{t}</option>)}</select></Field>
          <Field id="inq-state" label="Event state"><select id="inq-state" value={f.state} onChange={set("state")} data-testid="inquiry-state-select">{IN_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}</select></Field>
          <Field id="inq-city" label="City *" error={errors.city}><input id="inq-city" value={f.city} onChange={set("city")} maxLength={60} placeholder="e.g. Hyderabad" data-testid="inquiry-city-input" /></Field>
          <Field id="inq-guests" label="Estimated guests" error={errors.guests}><input id="inq-guests" inputMode="numeric" value={f.guests} onChange={set("guests")} placeholder="e.g. 250" data-testid="inquiry-guests-input" /></Field>
          <Field id="inq-date" label="Preferred date"><input id="inq-date" type="date" value={f.date} onChange={set("date")} data-testid="inquiry-date-input" /></Field>
          <Field id="inq-lang" label="Preferred language"><select id="inq-lang" value={f.language} onChange={set("language")} data-testid="inquiry-language-select">{LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.english}</option>)}</select></Field>
          <Field id="inq-name" label="Your name *" error={errors.name}><input id="inq-name" value={f.name} onChange={set("name")} maxLength={80} data-testid="inquiry-name-input" /></Field>
          <Field id="inq-email" label="Email *" error={errors.email}><input id="inq-email" type="email" value={f.email} onChange={set("email")} maxLength={120} data-testid="inquiry-email-input" /></Field>
          <Field id="inq-mobile" label="Mobile (+91)" error={errors.mobile}><input id="inq-mobile" inputMode="numeric" value={f.mobile} onChange={set("mobile")} maxLength={10} placeholder="10 digits" data-testid="inquiry-mobile-input" /></Field>
        </div>
        <Field id="inq-notes" label="Requirements"><textarea id="inq-notes" rows={3} value={f.notes} onChange={set("notes")} maxLength={1000} data-testid="inquiry-notes-input" /></Field>
        <label className="checkrow"><input type="checkbox" checked={f.consent} onChange={set("consent")} data-testid="inquiry-consent-checkbox" /> I consent to be contacted about this event request.</label>
        {errors.consent && <small className="err" data-testid="inquiry-consent-error">{errors.consent}</small>}
        <div className="modal-actions">
          <button type="button" className="btn outline" onClick={onClose} data-testid="inquiry-cancel-button">Cancel</button>
          <button type="submit" className="btn blue" disabled={busy} data-testid="inquiry-submit-button">{busy ? "Submitting…" : "Submit inquiry"}</button>
        </div>
      </form>
    </PortalModal>
  );
};
